import React, { useRef, useState } from "react";
import { cn } from "../../lib/utils";
import { STATUS_MAP, cropImage, downloadDataUrl, readAsDataUrl } from "../../lib/gamecraft";
import { UploadSimple, Download, Scissors, X, Sparkle } from "@phosphor-icons/react";

export function GlassCard({ className, children, hover, onClick, ...props }) {
  const Comp = onClick ? "button" : "div";
  return (
    <Comp
      onClick={onClick}
      className={cn("gc-card p-4 text-left", hover && "gc-card-hover transition-all", className)}
      {...props}
    >
      {children}
    </Comp>
  );
}

export function ProgressRing({ value = 0, size = 92, stroke = 9, label }) {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (Math.min(100, Math.max(0, value)) / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="currentColor" className="text-slate-200 dark:text-slate-700" strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke="var(--gc-primary)"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-lg font-bold leading-none" style={{ color: "var(--gc-text)" }}>{value}%</span>
        {label && <span className="text-[9px] mt-0.5" style={{ color: "var(--gc-muted)" }}>{label}</span>}
      </div>
    </div>
  );
}

export function StatusDot({ status = "neutral", size = 10, className }) {
  return (
    <span
      className={cn("inline-block rounded-full shrink-0", className)}
      style={{ width: size, height: size, background: STATUS_MAP[status]?.color || "#94a3b8" }}
      title={STATUS_MAP[status]?.label}
    />
  );
}

export function StatusSelect({ value, onChange }) {
  return (
    <div className="flex items-center gap-1">
      {Object.values(STATUS_MAP).map((s) => (
        <button
          key={s.id}
          type="button"
          title={s.label}
          onClick={() => onChange(s.id)}
          className={cn("w-5 h-5 rounded-full border-2 transition", value === s.id ? "scale-110" : "opacity-50 hover:opacity-100")}
          style={{ background: s.color, borderColor: value === s.id ? "var(--gc-text)" : "transparent" }}
        />
      ))}
    </div>
  );
}

export function TagInput({ tags = [], onChange, placeholder = "Añadir etiqueta" }) {
  const [val, setVal] = useState("");
  const add = () => {
    const t = val.trim().replace(/^#/, "");
    if (!t || tags.includes(t)) return;
    onChange([...tags, t]);
    setVal("");
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {tags.map((t) => (
        <span key={t} className="gc-chip">
          #{t}
          <button type="button" onClick={() => onChange(tags.filter((x) => x !== t))} className="opacity-60 hover:opacity-100">×</button>
        </span>
      ))}
      <input
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
        placeholder={placeholder}
        className="bg-transparent text-xs outline-none min-w-[90px]"
        style={{ color: "var(--gc-text)" }}
      />
    </div>
  );
}

export function FileDropzone({ accept, multiple, onFiles, label = "Subir archivos", icon }) {
  const ref = useRef(null);
  return (
    <>
      <input ref={ref} type="file" accept={accept} multiple={multiple} className="hidden" onChange={(e) => { onFiles(Array.from(e.target.files || [])); e.target.value = ""; }} />
      <button type="button" onClick={() => ref.current?.click()} className="gc-btn gc-btn-ghost h-9 px-3 text-xs">
        {icon || <UploadSimple size={14} />} {label}
      </button>
    </>
  );
}

export function ImageCropper({ src, onCrop, onClose }) {
  const imgRef = useRef(null);
  const [rect, setRect] = useState({ x: 0, y: 0, w: 64, h: 64 });
  const [drag, setDrag] = useState(null);
  const [ready, setReady] = useState(0);

  const rel = (e) => {
    const r = imgRef.current.getBoundingClientRect();
    const scaleX = imgRef.current.naturalWidth / r.width;
    const scaleY = imgRef.current.naturalHeight / r.height;
    return {
      x: Math.max(0, (e.clientX - r.left) * scaleX),
      y: Math.max(0, (e.clientY - r.top) * scaleY),
      scaleX, scaleY, r,
    };
  };

  const onDown = (e) => {
    const p = rel(e);
    setDrag({ x: p.x, y: p.y });
    setRect({ x: p.x, y: p.y, w: 1, h: 1 });
  };
  const onMove = (e) => {
    if (!drag) return;
    const p = rel(e);
    setRect({
      x: Math.min(drag.x, p.x),
      y: Math.min(drag.y, p.y),
      w: Math.abs(p.x - drag.x),
      h: Math.abs(p.y - drag.y),
    });
  };

  const apply = async () => {
    const out = await cropImage(src, rect);
    onCrop(out, rect);
  };

  const img = imgRef.current;
  const sx = img ? img.clientWidth / img.naturalWidth : 1;
  const sy = img ? img.clientHeight / img.naturalHeight : 1;

  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4">
      <div className="gc-card max-w-3xl w-full p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="font-semibold flex items-center gap-2"><Scissors size={16} /> Recortar imagen</div>
          <button onClick={onClose}><X size={18} /></button>
        </div>
        <p className="text-xs mb-3" style={{ color: "var(--gc-muted)" }}>Arrastra sobre la imagen para elegir el recorte (ideal para spritesheets).</p>
        <div className="relative inline-block max-h-[60vh] overflow-auto">
          <img
            ref={imgRef}
            src={src}
            alt="crop"
            onMouseDown={onDown}
            onMouseMove={onMove}
            onMouseUp={() => setDrag(null)}
            onLoad={() => setReady((n) => n + 1)}
            className="max-w-full select-none"
            style={{ imageRendering: "pixelated", cursor: "crosshair" }}
            draggable={false}
          />
          {img && (
            <div
              className="absolute border-2 border-sky-400 bg-sky-400/20 pointer-events-none"
              style={{ left: rect.x * sx, top: rect.y * sy, width: rect.w * sx, height: rect.h * sy }}
            />
          )}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <button className="gc-btn gc-btn-ghost h-9 px-4" onClick={onClose}>Cancelar</button>
          <button className="gc-btn gc-btn-primary h-9 px-4" onClick={apply}>Recortar</button>
        </div>
      </div>
    </div>
  );
}

export function AssetActions({ dataUrl, filename, onCropped, accept = "image/*" }) {
  const [cropSrc, setCropSrc] = useState(null);
  const onUpload = async (files) => {
    if (!files[0]) return;
    const url = await readAsDataUrl(files[0]);
    if (onCropped) onCropped(url, files[0].name);
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <FileDropzone accept={accept} onFiles={onUpload} label="Subir" />
      {dataUrl && (
        <>
          <button type="button" className="gc-btn gc-btn-ghost h-9 px-3 text-xs" onClick={() => downloadDataUrl(dataUrl, filename || "asset")}>
            <Download size={14} /> Descargar
          </button>
          {accept.includes("image") && (
            <button type="button" className="gc-btn gc-btn-ghost h-9 px-3 text-xs" onClick={() => setCropSrc(dataUrl)}>
              <Scissors size={14} /> Recortar
            </button>
          )}
        </>
      )}
      {cropSrc && (
        <ImageCropper
          src={cropSrc}
          onClose={() => setCropSrc(null)}
          onCrop={(out) => { onCropped?.(out, filename || "crop.png"); setCropSrc(null); }}
        />
      )}
    </div>
  );
}

export function SubNav({ items, value, onChange }) {
  return (
    <div className="flex flex-nowrap gap-1 p-1 rounded-2xl overflow-x-auto gc-hscroll" style={{ background: "var(--gc-surface-soft)", border: "1px solid var(--gc-border)" }}>
      {items.map((it) => (
        <button
          key={it.id}
          onClick={() => onChange(it.id)}
          className={cn("px-3 h-10 rounded-xl text-xs font-semibold transition shrink-0", value === it.id ? "text-white" : "")}
          style={value === it.id ? { background: "var(--gc-primary)" } : { color: "var(--gc-muted)" }}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

export function AIAssistantBar({ onSend, loading }) {
  const [q, setQ] = useState("");
  const chips = [
    { id: "guide", label: "Guía primer juego", action: "guide" },
    { id: "sprite", label: "Generar sprite", action: "sprite" },
    { id: "tileset", label: "Tileset del mundo", action: "tileset" },
    { id: "engine", label: "Probar en motor", action: "engine" },
    { id: "debt", label: "Tareas de deuda", action: "debt" },
    { id: "playlog", label: "Nota de playtest", action: "playlog" },
  ];
  const send = (text, action) => {
    const prompt = text || q;
    if (!action && !prompt.trim()) return;
    onSend(prompt, action);
    setQ("");
  };
  return (
    <div className="gc-glass px-4 py-3 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
      <div className="flex items-center gap-2 shrink-0 font-semibold text-sm">
        <Sparkle size={16} weight="fill" className="text-sky-500" /> Asistente IA
      </div>
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && send()}
        placeholder="¿En qué puedo ayudarte hoy?"
        className="gc-input h-10 px-4 flex-1 text-sm"
      />
      <div className="flex flex-wrap gap-1.5">
        {chips.map((c) => (
          <button key={c.id} onClick={() => send(c.label, c.action)} className="gc-chip hover:brightness-95">
            {c.label}
          </button>
        ))}
      </div>
      <button disabled={loading} onClick={() => send()} className="gc-btn gc-btn-primary h-10 px-4 shrink-0">
        {loading ? "..." : "Enviar"}
      </button>
    </div>
  );
}
